/**
 * AuditorReport — el informe comprimido.
 *
 * Todo lo que pinta sale de UNA llamada a Google que ya se pagó: la cabecera,
 * las cinco filas, el repaso de check/cruz y las frases sueltas vienen del mismo
 * `Place Details`. No hay ninguna petición extra por enseñar más.
 *
 * Lo único que viene de otro sitio son las líneas de benchmark, y esas salen de
 * NUESTRO parque conectado (`gbusiness/services/audit_benchmarks.py`). Son la
 * alternativa honesta a inventarse «~120 visitas que pierdes al mes»: un hecho
 * medido sobre fichas reales, comparando negocios de tamaño parecido, y dicho
 * como observación y no como promesa. Si a la cohorte no le llegó la muestra, la
 * lista viene vacía y aquí no se pinta nada — que es lo correcto.
 *
 * `ScoreRing` se importa del SEO Local en vez de duplicarse: son 85 líneas sin
 * una sola dependencia de sesión, de router ni de datos.
 */
import { AlertTriangle, ArrowUpRight, Check, ExternalLink, Minus, Star, X } from 'lucide-react';

import ScoreRing from '@/components/gbusiness/healthScore/ScoreRing';
import { placeTypeLabel } from '@/lib/placeTypes';

import {
  benchmarkText,
  blockRingColor,
  blockValueText,
  fill,
  highlightText,
  leverText,
  ringColor,
} from './auditorFormat';

export default function AuditorReport({ c, report, t }) {
  const r = c.report;
  const { place, score } = report;
  // Solo lo que NO está hecho. Cuatro chips verdes repetían lo que ya dicen las
  // filas de arriba y enterraban el único que importa: el rojo.
  const checklist = (score.checklist || []).filter((item) => item.done !== true);
  const levers = score.levers || [];
  const gap = score.gap || {};
  const highlights = report.highlights || [];
  const benchmarks = report.benchmarks || [];

  return (
    <div className="rounded-3xl border border-line bg-white soft-shadow overflow-hidden">
      {/* ── Cabecera: quién es, para que se reconozca de un vistazo ───────── */}
      <div className="px-6 py-5 border-b border-line">
        <p className="font-extrabold text-ink text-lg leading-tight">{place.name}</p>
        {place.address && <p className="text-sm text-slate-500">{place.address}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {place.primary_type && (
            <Badge>{placeTypeLabel(place.primary_type, t)}</Badge>
          )}
          {place.locality && <Badge>{place.locality}</Badge>}
          {/* Ni la nota ni el número de reseñas van aquí: los dicen ya sus
              filas, y repetirlos en la cabecera es ruido que además hace que el
              informe parezca más corto de lo que es. */}
          {place.maps_url && (
            <a
              href={place.maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-ink transition-colors"
            >
              {r.mapsLink} <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>

      {/* ── La puntuación, y el techo que da rellenar lo que falta ────────── */}
      <div className="px-6 py-6 flex flex-wrap items-center gap-5">
        <ScoreRing
          value={score.total}
          max={100}
          color={ringColor(score.color)}
          size={96}
          stroke={7}
          label={r.scoreTitle}
        >
          <span className="text-2xl font-extrabold text-ink">{score.total}</span>
        </ScoreRing>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-500">{r.scoreTitle}</p>
          <p className="text-xl font-extrabold text-ink">
            {(r.labels || {})[score.label] || score.label}
          </p>
          <p className="text-sm text-slate-500">{score.total} {r.outOf}</p>
        </div>

        {/* Dónde se pierden los puntos, no cuántos se pueden rellenar.
            El «techo alcanzable» que había aquí antes solo contaba perfil y
            fotos, así que a un negocio con la ficha completa le decía «+4, sube
            fotos» mientras perdía 26 puntos en reseñas: le escondía su mayor
            problema, que además es el que resolvemos. */}
        {gap.total > 0 && (
          <div className="ml-auto rounded-2xl bg-mist border border-line px-4 py-3 max-w-xs">
            <p className="text-xs font-semibold text-slate-500">{r.gapTitle}</p>
            <p className="text-sm text-ink leading-snug mt-0.5">
              <span className="font-extrabold">{fill(r.gapLine, { total: gap.total })}</span>
              {gap.reviews > 0 && (
                <> <span className="text-slate-600">{fill(r.gapReviews, { reviews: gap.reviews })}</span></>
              )}
            </p>
          </div>
        )}
      </div>

      {(score.warnings || []).length > 0 && (
        <div className="mx-6 mb-4 rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
          <div className="text-sm text-amber-800">
            {score.warnings.map((key) => (
              <p key={key}>{(r.warnings || {})[key] || key}</p>
            ))}
          </div>
        </div>
      )}

      {/* ── Las frases sueltas. Salen de las 5 reseñas que Google ya dio ──── */}
      {highlights.length > 0 && (
        <ul className="mx-6 mb-5 rounded-2xl bg-mist border border-line divide-y divide-line">
          {highlights.map((highlight) => (
            <li key={highlight.key} className="px-4 py-2.5 text-sm text-slate-600">
              {highlightText(highlight, c)}
            </li>
          ))}
        </ul>
      )}

      {/* ── Las cinco filas que puntúan ───────────────────────────────────── */}
      <ul className="px-6 pb-2 divide-y divide-line">
        {(score.blocks || []).map((block) => (
          <li key={block.key} className="py-3 flex items-center gap-4">
            <ScoreRing
              value={block.pts || 0}
              max={block.max}
              color={blockRingColor(block)}
              size={44}
              stroke={3}
              // Un anillo al 0 % diría «lo tienes mal». Cuando no se ha medido
              // nada, la pista va a rayas y sin arco, igual que en el SEO Local.
              dashed={block.pts === null || block.pts === undefined}
            />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-ink text-sm leading-snug">
                {(r.blocks || {})[block.key] || block.key}
              </p>
              <p className="text-sm text-slate-500">
                {blockValueText(block, c)}
                {/* La cifra va SIEMPRE al lado del aro. Un arco a medio llenar
                    de 44 px sin número no dice nada: es la convención del SEO
                    Local de la app —número dentro o al lado— y sin ella el aro
                    es decoración. */}
                {block.pts !== null && block.pts !== undefined && (
                  <span className="text-slate-400">
                    {' · '}{fill(r.points, { pts: block.pts, max: block.max })}
                  </span>
                )}
              </p>
            </div>
          </li>
        ))}
      </ul>

      {/* ── Lo que más subiría la nota ────────────────────────────────────
          Sale de la propia tabla de puntuación leída al revés: si el siguiente
          tramo de volumen está en 75 reseñas, llegar ahí suma lo que suma. Es
          aritmética verificable, no una estimación de tráfico — y en casi
          cualquier ficha la primera palanca son las reseñas, que es justo lo
          que el producto resuelve. */}
      {levers.length > 0 && (
        <div className="px-6 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
            {r.leversTitle}
          </p>
          <ul className="divide-y divide-line">
            {levers.map((lever) => (
              <li key={`${lever.key}-${lever.target}`} className="py-2.5 flex items-center gap-3">
                <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-xs font-bold text-emerald-700 shrink-0">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                  {fill(r.leverGain, { gain: lever.gain })}
                </span>
                <span className="min-w-0 flex-1 text-sm text-ink">
                  {leverText(lever, c)}
                </span>
                <span className="text-xs text-slate-400 shrink-0">
                  {fill(r.leverScore, { score: lever.score })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Lo que queda sin completar ────────────────────────────────────── */}
      {checklist.length > 0 && (
        <div className="px-6 pt-4 pb-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">
            {r.checklistTitle}
          </p>
          <div className="flex flex-wrap gap-2">
            {checklist.map((item) => (
              <ChecklistChip
                key={item.key}
                done={item.done}
                label={(r.checklist || {})[item.key] || item.key}
                unknownLabel={r.checklistUnknown}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Lo que dice nuestro parque. Observación, nunca promesa ────────── */}
      {benchmarks.length > 0 && (
        <div className="mx-6 mt-5 mb-1 rounded-2xl border border-line bg-mist px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
            {r.benchmarkTitle}
          </p>
          <ul className="space-y-1.5">
            {benchmarks.map((benchmark) => (
              <li key={benchmark.key} className="text-sm text-slate-600">
                {benchmarkText(benchmark, c)}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-slate-400">
            {fill(r.benchmarkSample, benchmarks[0].sample)}
          </p>
        </div>
      )}

      <div className="px-6 pb-5 pt-4 flex items-center gap-2 text-xs text-slate-400">
        <Star className="w-3.5 h-3.5" />
        <span>{r.source}</span>
      </div>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-mist border border-line px-2.5 py-1 text-xs font-semibold text-slate-600">
      {children}
    </span>
  );
}

/**
 * Un chip del repaso.
 *
 * `done === null` es «no lo sabemos», no «no lo tiene»: la frescura de las
 * reseñas se neutraliza cuando Google no ha devuelto ninguna. Ahí va una raya
 * gris y no una cruz roja, por lo mismo que el anillo va a rayas.
 *
 * El `data-state` está para que esa distinción se pueda afirmar en un test sin
 * contar clases de color: el verde y el rojo los comparte con el muro de abajo,
 * y contar `.text-emerald-700` mide otra cosa sin avisar.
 */
function ChecklistChip({ done, label, unknownLabel }) {
  if (done === null || done === undefined) {
    return (
      <span
        title={unknownLabel}
        data-state="unknown"
        className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-1.5 text-xs font-semibold text-slate-400"
      >
        <Minus className="w-3.5 h-3.5" /> {label}
      </span>
    );
  }
  return done ? (
    <span data-state="done" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50/60 px-3 py-1.5 text-xs font-semibold text-emerald-700">
      <Check className="w-3.5 h-3.5" /> {label}
    </span>
  ) : (
    <span data-state="missing" className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50/60 px-3 py-1.5 text-xs font-semibold text-rose-700">
      <X className="w-3.5 h-3.5" /> {label}
    </span>
  );
}
